// Admin highlight bars for the v2 shop floor — a port of the v1 terminal's
// "Global Highlights" / "Product Code Rules" editors. Same endpoint
// (/api/shop-floor/highlights/), same rules, same 800ms debounced
// full-replacement save, so one set of settings drives both terminals and
// admins can manage it from v2 (required once the v1 page redirects here
// after cutover).
//
// ADMIN ONLY: non-staff render nothing at all — the floor terminals just
// see the highlights applied on the rows, not the configuration (the
// summary chips included; v1 showed chips to everyone, but on the floor
// that read as "settings" clutter).
// Every change is pushed up via onRulesChanged immediately (optimistically,
// matching what the debounced save will persist) so the rows re-highlight
// live while typing.
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Collapse, Divider, IconButton,
  InputAdornment, TextField, Typography,
} from '@mui/material';
import { Add, Close, FormatPaint, Search } from '@mui/icons-material';
import { shopFloorAPI } from '../../../../services/api';

const MARK_STYLE = {
  backgroundColor: '#ffeb3b', padding: '0 3px', borderRadius: '2px',
};

export default function HighlightBarsV2({ isStaff, onRulesChanged }) {
  const [globalHighlights, setGlobalHighlights] = useState([]);
  const [highlightRules, setHighlightRules] = useState([]);
  const [showGlobalBar, setShowGlobalBar] = useState(false);
  const [showRulesBar, setShowRulesBar] = useState(false);
  const saveTimeout = useRef(null);
  const onRulesChangedRef = useRef(onRulesChanged);
  onRulesChangedRef.current = onRulesChanged;

  useEffect(() => {
    shopFloorAPI.getHighlights().then((res) => {
      const all = res.data || [];
      setGlobalHighlights(
        all.filter((r) => r.product_code === '*').map((r) => r.highlight_text),
      );
      setHighlightRules(
        all.filter((r) => r.product_code !== '*').map((r) => ({
          productCode: r.product_code,
          highlightText: r.highlight_text,
        })),
      );
      if (onRulesChangedRef.current) onRulesChangedRef.current(all);
    }).catch(() => { /* no rules is fine */ });
    return () => { if (saveTimeout.current) clearTimeout(saveTimeout.current); };
  }, []);

  const buildRows = (productRules, globals) => {
    const rows = productRules
      .filter((r) => r.productCode.trim() && r.highlightText.trim())
      .map((r) => ({
        product_code: r.productCode.trim(),
        highlight_text: r.highlightText.trim(),
      }));
    for (const g of globals) {
      const trimmed = g.trim();
      if (trimmed) rows.push({ product_code: '*', highlight_text: trimmed });
    }
    return rows;
  };

  // Debounced save of the FULL rule set (mirrors v1's saveAllHighlights).
  const saveAll = (productRules, globals) => {
    if (!isStaff) return;
    const rows = buildRows(productRules, globals);
    if (onRulesChangedRef.current) onRulesChangedRef.current(rows);
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      shopFloorAPI.saveHighlights(rows).catch(() => {});
    }, 800);
  };

  // --- Global highlight CRUD (adding an empty row doesn't save) ---
  const addGlobalHighlight = () => setGlobalHighlights((g) => [...g, '']);
  const updateGlobalHighlight = (index, value) => {
    const next = globalHighlights.map((g, i) => (i === index ? value : g));
    setGlobalHighlights(next);
    saveAll(highlightRules, next);
  };
  const removeGlobalHighlight = (index) => {
    const next = globalHighlights.filter((_, i) => i !== index);
    setGlobalHighlights(next);
    saveAll(highlightRules, next);
  };

  // --- Product-code rule CRUD ---
  const addHighlightRule = () => (
    setHighlightRules((r) => [...r, { productCode: '', highlightText: '' }])
  );
  const updateHighlightRule = (index, field, value) => {
    const next = highlightRules.map((rule, i) => (
      i === index ? { ...rule, [field]: value } : rule
    ));
    setHighlightRules(next);
    saveAll(next, globalHighlights);
  };
  const removeHighlightRule = (index) => {
    const next = highlightRules.filter((_, i) => i !== index);
    setHighlightRules(next);
    saveAll(next, globalHighlights);
  };

  const activeGlobals = globalHighlights.filter((g) => g.trim());
  const activeRules = highlightRules.filter(
    (r) => r.productCode.trim() && r.highlightText.trim(),
  );

  // Highlight CONFIGURATION is admin-only — the floor sees only the
  // highlighted rows. (Rule loading above still ran, feeding onRulesChanged,
  // so non-staff rows highlight fine.)
  if (!isStaff) return null;

  return (
    <Card sx={{ mb: 2 }} data-testid="highlight-bars">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Toggle buttons + summary chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {isStaff && (
            <>
              <Button
                startIcon={<Search />}
                onClick={() => {
                  setShowGlobalBar((prev) => !prev);
                  if (!showGlobalBar && globalHighlights.length === 0) {
                    setGlobalHighlights(['']);
                  }
                }}
                size="small"
                variant={showGlobalBar ? 'contained' : 'outlined'}
                color="warning"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                data-testid="btn-global-highlights"
              >
                Global Highlights {activeGlobals.length > 0 ? `(${activeGlobals.length})` : ''}
              </Button>
              <Button
                startIcon={<FormatPaint />}
                onClick={() => {
                  setShowRulesBar((prev) => !prev);
                  if (!showRulesBar && highlightRules.length === 0) {
                    setHighlightRules([{ productCode: '', highlightText: '' }]);
                  }
                }}
                size="small"
                variant={showRulesBar ? 'contained' : 'outlined'}
                color="warning"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                data-testid="btn-product-code-rules"
              >
                Product Code Rules {activeRules.length > 0 ? `(${activeRules.length})` : ''}
              </Button>
            </>
          )}
          {/* Summary chips when collapsed (visible to everyone) */}
          {!showGlobalBar && activeGlobals.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {activeGlobals.map((g, i) => (
                <Chip
                  key={`g-${i}`}
                  label={<mark style={MARK_STYLE}>{g.trim()}</mark>}
                  size="small"
                  variant="outlined"
                  color="warning"
                />
              ))}
            </Box>
          )}
          {!showRulesBar && activeRules.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {activeRules.map((rule, i) => (
                <Chip
                  key={`p-${i}`}
                  label={(
                    <>
                      <strong>{rule.productCode}</strong>
                      {' → '}
                      <mark style={MARK_STYLE}>{rule.highlightText}</mark>
                    </>
                  )}
                  size="small"
                  variant="outlined"
                  color="warning"
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Global highlights editor (admin only) */}
        {isStaff && (
          <Collapse in={showGlobalBar}>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Highlight text across all work orders (persisted, visible to all users)
            </Typography>
            <Box>
              {globalHighlights.map((text, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Text to Highlight"
                    placeholder="e.g. 635"
                    value={text}
                    onChange={(e) => updateGlobalHighlight(index, e.target.value)}
                    sx={{ flex: 1, maxWidth: 300 }}
                    InputProps={{
                      endAdornment: text.trim() ? (
                        <InputAdornment position="end">
                          <mark style={{ ...MARK_STYLE, fontSize: '0.8rem' }}>{text.trim()}</mark>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeGlobalHighlight(index)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button startIcon={<Add />} size="small" onClick={addGlobalHighlight} variant="outlined" sx={{ textTransform: 'none' }}>
                  Add
                </Button>
                {globalHighlights.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => { setGlobalHighlights([]); saveAll(highlightRules, []); }}
                    color="error"
                    variant="text"
                    sx={{ textTransform: 'none' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>
          </Collapse>
        )}

        {/* Product code rules editor (admin only) */}
        {isStaff && (
          <Collapse in={showRulesBar}>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Highlight specific text within matching product codes (persisted, visible to all users)
            </Typography>
            <Box>
              {highlightRules.map((rule, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Product Code"
                    placeholder="e.g. EN/AA25"
                    value={rule.productCode}
                    onChange={(e) => updateHighlightRule(index, 'productCode', e.target.value)}
                    sx={{ flex: 1, maxWidth: 250 }}
                  />
                  <TextField
                    size="small"
                    label="Text to Highlight"
                    placeholder="e.g. 635"
                    value={rule.highlightText}
                    onChange={(e) => updateHighlightRule(index, 'highlightText', e.target.value)}
                    sx={{ flex: 1, maxWidth: 250 }}
                    InputProps={{
                      endAdornment: rule.highlightText ? (
                        <InputAdornment position="end">
                          <mark style={{ ...MARK_STYLE, fontSize: '0.8rem' }}>{rule.highlightText}</mark>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeHighlightRule(index)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button startIcon={<Add />} size="small" onClick={addHighlightRule} variant="outlined" sx={{ textTransform: 'none' }}>
                  Add Rule
                </Button>
                {highlightRules.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => { setHighlightRules([]); saveAll([], globalHighlights); }}
                    color="error"
                    variant="text"
                    sx={{ textTransform: 'none' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
}
